/**
 * Tells "our own write" apart from "someone else's" for realtime events.
 *
 * A consignee import made in this tab also produces consignee_master_changed,
 * and that broadcast can arrive back through the websocket before or shortly
 * after the HTTP response. While the write is in flight, and for a short echo
 * window after it succeeds, events are treated as our own and ignored by
 * "a colleague changed it" UI. A failed write opens no window: nothing of
 * ours was broadcast.
 */
export const OWN_WRITE_ECHO_MS = 3000;

export interface OwnWriteGuard {
  begin: () => void;
  succeed: () => void;
  fail: () => void;
  isInFlight: () => boolean;
  /** True while a write is in flight or within the echo window after a success. */
  isSuppressed: () => boolean;
}

export const createOwnWriteGuard = (
  echoMs: number = OWN_WRITE_ECHO_MS,
  now: () => number = Date.now
): OwnWriteGuard => {
  let inFlight = 0;
  let lastSuccessAt = Number.NEGATIVE_INFINITY;
  return {
    begin: () => {
      inFlight += 1;
    },
    succeed: () => {
      inFlight = Math.max(0, inFlight - 1);
      lastSuccessAt = now();
    },
    fail: () => {
      inFlight = Math.max(0, inFlight - 1);
    },
    isInFlight: () => inFlight > 0,
    isSuppressed: () => inFlight > 0 || now() - lastSuccessAt < echoMs,
  };
};
